(function () {
  if (window.__cartShippingInit) return;
  window.__cartShippingInit = true;

  const STORAGE_KEY = 'shipping_country';
  const SECTION_ID = 'cart-shipping';
  let controller = null;
  let initialized = false;

  /** @type {Record<string, [number, string, string]>} */
  const MONEY_FORMATS = {
    amount: [2, ',', '.'],
    amount_no_decimals: [0, ',', '.'],
    amount_with_comma_separator: [2, '.', ','],
    amount_no_decimals_with_comma_separator: [0, '.', ','],
    amount_with_apostrophe_separator: [2, "'", '.'],
    amount_no_decimals_with_space_separator: [0, ' ', '.'],
    amount_with_space_separator: [2, ' ', ','],
  };

  /**
   * Format cents into a money string using the shop's money format.
   * @param {number} cents
   * @returns {string}
   */
  function formatMoney(cents) {
    if (typeof Theme === 'undefined' || !Theme.moneyFormat) {
      return (cents / 100).toFixed(2);
    }

    const amount = cents / 100;
    const format = Theme.moneyFormat;

    return format.replace(/\{\{\s*(amount\w*)\s*\}\}/, (_, placeholder) => {
      const [precision, thousands, decimal] = MONEY_FORMATS[placeholder] || [2, ',', '.'];
      const fixed = amount.toFixed(precision);
      const parts = fixed.split('.');
      const intPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return precision > 0 ? intPart + decimal + parts[1] : intPart;
    });
  }

  function t(key) {
    return Theme?.translations?.[key] || '';
  }

  async function detectCountry() {
    const cached = sessionStorage.getItem(STORAGE_KEY);
    if (cached) return cached;

    try {
      const res = await fetch('/browsing_context_suggestions.json');
      if (!res.ok) throw new Error(res.status);
      const data = await res.json();
      const country = data?.detected_values?.country?.handle;
      if (country) {
        sessionStorage.setItem(STORAGE_KEY, country);
        return country;
      }
    } catch (_) {
      /* detection failed — cannot estimate shipping */
    }

    return null;
  }

  async function fetchCart() {
    try {
      const res = await fetch('/cart.js');
      if (!res.ok) throw new Error(res.status);
      return await res.json();
    } catch (_) {
      return null;
    }
  }

  async function ensureCartAttribute(country, cart) {
    if (!cart || cart.attributes?.[STORAGE_KEY] === country) return;

    try {
      const updateRes = await fetch(Theme.routes.cart_update_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ attributes: { [STORAGE_KEY]: country } }),
      });
      if (!updateRes.ok) throw new Error(updateRes.status);
    } catch (_) {
      /* attribute update failed — section will render fallback JSON */
    }
  }

  function showFallback() {
    document.querySelectorAll('[data-shipping-cost-target]').forEach((el) => {
      el.textContent = el.dataset.shippingFallback || '';
    });
    document.querySelectorAll('[data-shipping-banner-target]').forEach((el) => {
      el.hidden = true;
    });
  }

  /**
   * Build the shipping cost line as a DocumentFragment (avoids innerHTML).
   * @param {object} shipping
   * @returns {DocumentFragment}
   */
  function buildCostLine(shipping) {
    const frag = document.createDocumentFragment();

    if (shipping.zone_miss) {
      const span = document.createElement('span');
      span.textContent = t('shipping_checkout');
      frag.appendChild(span);
      return frag;
    }

    if (shipping.shipping_cents === 0) {
      const label = document.createElement('span');
      label.textContent = t('shipping_label');
      frag.appendChild(label);

      frag.appendChild(document.createTextNode(' '));

      const value = document.createElement('span');
      if (shipping.savings_cents > 0) {
        const strikethrough = document.createElement('s');
        strikethrough.textContent = formatMoney(shipping.savings_cents);
        value.appendChild(strikethrough);
        value.appendChild(document.createTextNode(' '));
        const freeSpan = document.createElement('span');
        freeSpan.className = 'cart__shipping-free';
        freeSpan.textContent = t('shipping_free');
        value.appendChild(freeSpan);
      } else {
        value.className = 'cart__shipping-free';
        value.textContent = t('shipping_free');
      }
      frag.appendChild(value);
      return frag;
    }

    const label = document.createElement('span');
    label.textContent = t('shipping_label');
    frag.appendChild(label);

    frag.appendChild(document.createTextNode(' '));

    const value = document.createElement('span');
    value.textContent = formatMoney(shipping.shipping_cents);
    frag.appendChild(value);
    return frag;
  }

  /**
   * Build the banner message from JSON data.
   * @param {object} shipping
   * @returns {string|null} null means hide the banner
   */
  function buildBannerMessage(shipping) {
    if (shipping.zone_miss) return null;

    if (shipping.remaining_cents > 0) {
      const amount = formatMoney(shipping.remaining_cents);
      return t('shipping_free_remaining').replaceAll('__AMOUNT__', `<strong>${amount}</strong>`);
    }

    if (shipping.shipping_cents === 0) {
      return t('shipping_free_achieved');
    }

    return null;
  }

  async function fetchAndInject() {
    if (controller) controller.abort();
    controller = new AbortController();

    try {
      const res = await fetch(`/?sections=${SECTION_ID}`, {
        signal: controller.signal,
      });
      if (!res.ok) throw new Error(res.status);
      const sections = await res.json();
      const raw = sections[SECTION_ID];
      if (!raw) { showFallback(); return; }

      // Extract JSON from the <script type="application/json"> wrapper
      const doc = new DOMParser().parseFromString(raw, 'text/html');
      const jsonScript = doc.querySelector('script[type="application/json"]');
      if (!jsonScript) { showFallback(); return; }
      const shipping = JSON.parse(jsonScript.textContent);

      // Inject cost line into totals area
      const costFrag = buildCostLine(shipping);
      document.querySelectorAll('[data-shipping-cost-target]').forEach((el) => {
        el.textContent = '';
        el.appendChild(costFrag.cloneNode(true));
      });

      // Update checkout button price to include shipping
      if (!shipping.zone_miss && shipping.checkout_total) {
        document.querySelectorAll('[data-checkout-price-target]').forEach((el) => {
          el.textContent = shipping.checkout_total;
        });
      }

      // Inject banner message
      const bannerMessage = buildBannerMessage(shipping);
      document.querySelectorAll('[data-shipping-banner-target]').forEach((el) => {
        if (bannerMessage) {
          el.innerHTML = bannerMessage;
          el.hidden = false;
        } else {
          el.innerHTML = '';
          el.hidden = true;
        }
      });
    } catch (e) {
      if (e.name !== 'AbortError') {
        console.error('Shipping estimate error:', e);
        showFallback();
      }
    }
  }

  /**
   * Detect country and set cart attribute before fetching.
   * detectCountry() already caches in sessionStorage, so repeat calls are cheap.
   * @returns {boolean} true if country was resolved
   */
  async function prepareCountry() {
    const country = await detectCountry();
    if (!country) return false;
    const cart = await fetchCart();
    await ensureCartAttribute(country, cart);
    return true;
  }

  function deferredFetchAndInject() {
    setTimeout(async () => {
      if (!document.querySelector('[data-shipping-cost-target]')) return;
      if (await prepareCountry()) {
        await fetchAndInject();
      } else {
        showFallback();
      }
    }, 0);
  }

  async function init() {
    if (initialized) return;
    initialized = true;

    // Register listeners immediately so cart changes always trigger updates,
    // even if the initial fetch fails or the cart starts empty.
    document.addEventListener('cart:update', deferredFetchAndInject);
    document.addEventListener('discount:update', deferredFetchAndInject);

    const cartCountEl = document.querySelector('[ref="cartItemCount"]');
    if (cartCountEl && parseInt(cartCountEl.textContent, 10) === 0) {
      showFallback();
      return;
    }

    // Parallelize country detection and cart fetch to reduce waterfall
    const [country, cart] = await Promise.all([detectCountry(), fetchCart()]);
    if (!country) {
      showFallback();
      return;
    }
    await ensureCartAttribute(country, cart);
    await fetchAndInject();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
